import React from "react";
import MaterialIcon from "../MaterialIcon";

export function formatTicketDate(iso, locale) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale === "de" ? "de-DE" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_STYLES = {
  open: "bg-yellow-500/15 text-yellow-800",
  answered: "bg-secondary-container text-on-secondary-container",
  closed: "bg-surface-container text-on-surface-variant",
};

export const TicketStatusBadge = ({ status, t }) => {
  const labels = {
    open: t("support.statusOpen"),
    answered: t("support.statusAnswered"),
    closed: t("support.statusClosed"),
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 font-label-sm font-semibold ${
        STATUS_STYLES[status] || STATUS_STYLES.open
      }`}
    >
      {labels[status] || status}
    </span>
  );
};

export const TicketCategoryBadge = ({ category, t }) => {
  const isFeedback = category === "feedback";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-label-sm font-semibold ${
        isFeedback
          ? "bg-tertiary-container/60 text-on-surface"
          : "bg-primary/10 text-primary"
      }`}
    >
      <MaterialIcon
        name={isFeedback ? "lightbulb" : "support_agent"}
        className="text-[14px]"
      />
      {isFeedback ? t("support.categoryFeedbackShort") : t("support.categorySupportShort")}
    </span>
  );
};

/**
 * Message thread. `perspective` controls labels:
 * "client" — admin messages labeled "Support team", own labeled "You"
 * "admin"  — client messages labeled with the author's name
 */
export const TicketMessages = ({ messages, t, locale, perspective = "client" }) => (
  <ul className="space-y-3">
    {messages.map((msg) => {
      const fromAdmin = msg.isFromAdmin;
      const label = fromAdmin
        ? perspective === "admin"
          ? msg.authorName || t("support.supportTeam")
          : t("support.supportTeam")
        : perspective === "client"
          ? t("support.you")
          : msg.authorName || "—";
      return (
        <li
          key={msg.id}
          className={`rounded-xl border p-4 ${
            fromAdmin
              ? "border-secondary/20 bg-secondary-container/25"
              : "border-outline-variant/30 bg-surface-container-lowest"
          }`}
        >
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 font-label-sm font-semibold text-on-surface">
              <MaterialIcon
                name={fromAdmin ? "support_agent" : "forum"}
                className={`text-[15px] ${fromAdmin ? "text-secondary" : "text-on-surface-variant"}`}
              />
              {label}
            </span>
            <span className="font-label-sm text-outline">
              {formatTicketDate(msg.createdAt, locale)}
            </span>
          </div>
          <p className="whitespace-pre-wrap font-body-md text-on-surface">{msg.body}</p>
        </li>
      );
    })}
  </ul>
);
